import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Button, Form, Input, Modal, Table, Space, notification, Popconfirm, Tag, Switch, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import typeService from '../services/typeService';
import { Type } from '../types/type';
import { DishService } from '../services/dishService';
import { Dish } from '../types/dish';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';

const { Search } = Input;
const dishService = new DishService();

const CACHE_TTL = 60 * 1000;

type CacheEntry<T> = {
  data: T[];
  timestamp: number;
};

let typesCache: CacheEntry<Type> | null = null;
let typesRequest: Promise<Type[]> | null = null;
let dishesCache: CacheEntry<Dish> | null = null;
let dishesRequest: Promise<Dish[]> | null = null;

const isCacheValid = <T,>(cache: CacheEntry<T> | null) =>
  !!cache && Date.now() - cache.timestamp < CACHE_TTL;

const TypeManagement: React.FC = () => {
  const isMountedRef = useRef(true);
  const [types, setTypes] = useState<Type[]>([]);
  const [dishes, setDishes] = useState<Dish[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingType, setEditingType] = useState<Type | null>(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [isTourOpen, setIsTourOpen] = useState(false);

  // Tour refs
  const titleRef = useRef(null);
  const searchRef = useRef(null);
  const addButtonRef = useRef(null);
  const tableRef = useRef(null);

  // Tour steps
  const tourSteps: TourProps['steps'] = [
    {
      title: 'Quản lý loại món',
      description: 'Chào mừng bạn đến với trang Quản lý loại món! Đây là nơi bạn có thể tạo và quản lý các loại món ăn để phân loại chi tiết hơn trong thực đơn.',
      target: () => titleRef.current,
    },
    {
      title: 'Tìm kiếm loại món',
      description: 'Sử dụng thanh tìm kiếm này để tìm loại món theo mã hoặc tên loại món nhanh chóng.',
      target: () => searchRef.current,
    },
    {
      title: 'Thêm loại món mới',
      description: 'Nhấn vào đây để tạo một loại món mới. Bạn sẽ cần nhập mã loại món, tên loại món và trạng thái hiển thị.',
      target: () => addButtonRef.current,
    },
    {
      title: 'Bảng danh sách loại món',
      description: 'Bảng hiển thị tất cả các loại món đã tạo, bao gồm mã loại, tên loại, trạng thái và các món ăn đang áp dụng loại này. Bạn có thể chỉnh sửa hoặc xóa loại từ đây.',
      target: () => tableRef.current,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn có thể tạo và quản lý các loại món để phân loại thực đơn một cách chi tiết và có hệ thống.',
      target: () => titleRef.current,
    },
  ];

  const updateTypesState = useCallback((updater: (prev: Type[]) => Type[]) => {
    setTypes((prev) => {
      const next = updater(prev);
      typesCache = {
        data: next,
        timestamp: Date.now(),
      };
      return next;
    });
  }, []);

  const fetchTypes = useCallback(async (force = false): Promise<Type[]> => {
    if (!force && isCacheValid(typesCache)) {
      if (isMountedRef.current) {
        setTypes(typesCache!.data);
      }
      return typesCache!.data;
    }

    if (typesRequest) {
      const sharedData = await typesRequest;
      if (isMountedRef.current) {
        setTypes(sharedData);
      }
      return sharedData;
    }

    if (isMountedRef.current) {
      setLoading(true);
    }
    typesRequest = typeService.getAllTypes();

    try {
      const fetchedTypes = await typesRequest;
      typesCache = {
        data: fetchedTypes,
        timestamp: Date.now(),
      };
      if (isMountedRef.current) {
        setTypes(fetchedTypes);
      }
      return fetchedTypes;
    } catch (error) {
      console.error('Error fetching types:', error);
      if (isMountedRef.current) {
        notification.error({
          message: 'Lỗi',
          description: 'Không thể tải danh sách loại món.',
        });
      }
      return [];
    } finally {
      typesRequest = null;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const fetchDishes = useCallback(async (force = false): Promise<Dish[]> => {
    if (!force && isCacheValid(dishesCache)) {
      if (isMountedRef.current) {
        setDishes(dishesCache!.data);
      }
      return dishesCache!.data;
    }

    if (dishesRequest) {
      const sharedData = await dishesRequest;
      if (isMountedRef.current) {
        setDishes(sharedData);
      }
      return sharedData;
    }

    dishesRequest = dishService.getAllDishes();

    try {
      const fetchedDishes = await dishesRequest;
      dishesCache = {
        data: fetchedDishes,
        timestamp: Date.now(),
      };
      if (isMountedRef.current) {
        setDishes(fetchedDishes);
      }
      return fetchedDishes;
    } catch (error) {
      console.error('Error fetching dishes:', error);
      return [];
    } finally {
      dishesRequest = null;
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    void Promise.all([fetchTypes(), fetchDishes()]);

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchDishes, fetchTypes]);

  const handleAddOrUpdateType = async (values: Type) => {
    try {
      if (editingType) {
        await typeService.updateType(editingType.id!, values);
        updateTypesState((prev) => prev.map((type) => (
          type.id === editingType.id
            ? { ...type, ...values }
            : type
        )));
        notification.success({
          message: 'Thành công',
          description: 'Loại món đã được cập nhật thành công!',
        });
      } else {
        const createdType = await typeService.addType(values);
        updateTypesState((prev) => [createdType, ...prev]);
        notification.success({
          message: 'Thành công',
          description: 'Loại món mới đã được thêm thành công!',
        });
      }
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error saving type:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể lưu loại món.',
      });
    }
  };

  const handleEdit = (type: Type) => {
    setEditingType(type);
    form.setFieldsValue(type);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    // Kiểm tra xem có món ăn nào đang sử dụng loại món này không
    const appliedDishes = dishes.filter(dish => dish.type === id);

    if (appliedDishes.length > 0) {
      notification.error({
        message: 'Không thể xóa',
        description: `Loại món này đang được sử dụng bởi ${appliedDishes.length} món ăn. Vui lòng thay đổi loại món của các món ăn trước khi xóa.`,
      });
      return;
    }

    try {
      await typeService.deleteType(id);
      updateTypesState((prev) => prev.filter((type) => type.id !== id));
      notification.success({
        message: 'Thành công',
        description: 'Loại món đã được xóa thành công!',
      });
    } catch (error) {
      console.error('Error deleting type:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa loại món.',
      });
    }
  };

  const handleDeleteAll = async () => {
    if (types.length === 0) {
      notification.info({
        message: 'Không có dữ liệu',
        description: 'Hiện không có loại món nào để xóa.',
      });
      return;
    }

    const usedTypeIds = new Set(dishes.map((dish) => dish.type).filter(Boolean));
    const deletableTypes = types.filter((type) => type.id && !usedTypeIds.has(type.id));
    const blockedCount = types.length - deletableTypes.length;

    if (deletableTypes.length === 0) {
      notification.error({
        message: 'Không thể xóa',
        description: 'Tất cả loại món hiện đang được món ăn sử dụng.',
      });
      return;
    }

    setDeletingAll(true);
    try {
      const results = await Promise.allSettled(
        deletableTypes.map((type) => typeService.deleteType(type.id!))
      );

      const deletedCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedCount = results.length - deletedCount;

      if (deletedCount > 0) {
        const deletedIds = new Set(
          deletableTypes
            .filter((_, index) => results[index].status === 'fulfilled')
            .map((type) => type.id)
        );
        updateTypesState((prev) => prev.filter((type) => !type.id || !deletedIds.has(type.id)));
      }

      if (failedCount === 0) {
        notification.success({
          message: 'Thành công',
          description: blockedCount > 0
            ? `Đã xóa ${deletedCount} loại món. Bỏ qua ${blockedCount} loại đang được sử dụng.`
            : `Đã xóa ${deletedCount} loại món.`,
        });
      } else {
        notification.warning({
          message: 'Xóa chưa hoàn tất',
          description: `Đã xóa ${deletedCount} loại món, thất bại ${failedCount}. ${blockedCount > 0 ? `Bỏ qua ${blockedCount} loại đang được sử dụng.` : ''}`,
        });
      }
    } catch (error) {
      console.error('Error deleting all types:', error);
      notification.error({
        message: 'Lỗi',
        description: 'Không thể xóa tất cả loại món.',
      });
    } finally {
      setDeletingAll(false);
    }
  };

  const handleAddNew = () => {
    setEditingType(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true }); // Set default value for isActive
    setModalVisible(true);
  };

  const dishNamesByTypeId = useMemo(() => {
    const map = new Map<string, Dish[]>();
    dishes.forEach((dish) => {
      if (!dish.type) return;
      const current = map.get(dish.type) || [];
      current.push(dish);
      map.set(dish.type, current);
    });
    return map;
  }, [dishes]);

  const handleToggleTypeActive = useCallback(async (record: Type, checked: boolean) => {
    if (!record.id) return;

    updateTypesState((prev) => prev.map((type) => (
      type.id === record.id ? { ...type, isActive: checked } : type
    )));

    try {
      await typeService.updateType(record.id, { ...record, isActive: checked });
    } catch (error) {
      updateTypesState((prev) => prev.map((type) => (
        type.id === record.id ? { ...type, isActive: !checked } : type
      )));
      notification.error({
        message: 'Lỗi',
        description: 'Không thể cập nhật trạng thái loại món.',
      });
    }
  }, [updateTypesState]);

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1
    },
    {
      title: 'Mã loại món',
      dataIndex: 'code',
      key: 'code',
      width: 150,
    },
    {
      title: 'Tên loại món',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Trạng thái',
      key: 'isActive',
      dataIndex: 'isActive',
      width: 120,
      render: (isActive: boolean, record: any) => (
        <Switch
          checkedChildren="Hiển thị"
          unCheckedChildren="Ẩn"
          checked={isActive}
          onChange={(checked) => handleToggleTypeActive(record, checked)}
        />
      )
    },
    {
      title: 'Món ăn đang áp dụng',
      key: 'appliedDishes',
      width: 400,
      render: (_: any, record: Type) => {
        const appliedDishes = dishNamesByTypeId.get(record.id || '') || [];
        
        if (appliedDishes.length === 0) {
          return <span style={{ color: '#999' }}>Không có món ăn nào</span>;
        }

        const displayedDishes = appliedDishes.slice(0, 3);
        const remainingDishes = appliedDishes.slice(3);

        return (
          <Space wrap>
            {displayedDishes.map(dish => (
              <Tag color="blue" key={dish.id}>
                {dish.name}
              </Tag>
            ))}
            {remainingDishes.length > 0 && (
              <Tag color="purple">+{remainingDishes.length} món khác</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right' as const,
      width: 120,
      render: (_: any, record: Type) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Xóa loại món"
            description="Bạn có chắc chắn muốn xóa loại món này?"
            onConfirm={() => handleDelete(record.id!)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredTypes = useMemo(() => (
    searchTerm
      ? types.filter(type =>
        type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type.code.toLowerCase().includes(searchTerm.toLowerCase())
      )
      : types
  ), [searchTerm, types]);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1 ref={titleRef}>Quản lý loại món</h1>
          <Button
            type="primary"
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsTourOpen(true)}
            style={{ marginLeft: 8 }}
          >
            Hướng dẫn
          </Button>
          <Tag color="blue">{types.length} loại món</Tag>
        </div>

        <div className="admin-page-actions">
          <div className="admin-search-filters">
            <Search
              placeholder="Tìm kiếm theo mã hoặc tên loại món"
              allowClear
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 300 }}
              ref={searchRef}
            />
          </div>

          <Popconfirm
            title="Xóa toàn bộ loại món"
            description="Hệ thống sẽ chỉ xóa các loại chưa được món ăn sử dụng. Bạn có chắc chắn muốn tiếp tục?"
            onConfirm={handleDeleteAll}
            okText="Xóa hết"
            cancelText="Hủy"
            okButtonProps={{ danger: true, loading: deletingAll }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deletingAll}
              disabled={deletingAll || types.length === 0}
            >
              Xóa tất cả
            </Button>
          </Popconfirm>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddNew}
            className="admin-button"
            ref={addButtonRef}
          >
            Thêm loại món mới
          </Button>
        </div>
      </div>

      <div className="admin-form-section">
        <div className="admin-table-container">
          <Table
            className="admin-table"
            columns={columns}
            dataSource={filteredTypes}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1500 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Tổng số: ${total} loại món`,
            }}
            ref={tableRef}
          />
        </div>
      </div>

      <Modal
        title={editingType ? "Sửa thông tin loại món" : "Thêm loại món mới"}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        className="admin-modal"
      >
        <div className="admin-form-container">
          <Form 
            form={form} 
            onFinish={handleAddOrUpdateType} 
            layout="vertical"
            initialValues={{ isActive: true }}
          >
            <div className="admin-form-section">
              <Form.Item
                name="code"
                label="Mã loại món"
                rules={[{ required: true, message: 'Vui lòng nhập mã loại món!' }]}
              >
                <Input placeholder="Nhập mã loại món" />
              </Form.Item>

              <Form.Item
                name="name"
                label="Tên loại món"
                rules={[{ required: true, message: 'Vui lòng nhập tên loại món!' }]}
              >
                <Input placeholder="Nhập tên loại món" />
              </Form.Item>

              <Form.Item
                name="isActive"
                label="Trạng thái"
                valuePropName="checked"
              >
                <Switch checkedChildren="Hiển thị" unCheckedChildren="Ẩn" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="admin-button"
                >
                  {editingType ? 'Cập nhật' : 'Thêm mới'}
                </Button>
              </Form.Item>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Tour component */}
      <ConfigProvider locale={viVN}>
        <Tour
          open={isTourOpen}
          onClose={() => setIsTourOpen(false)}
          steps={tourSteps}
          indicatorsRender={(current, total) => (
            <span>{current + 1} / {total}</span>
          )}
          type="primary"
          arrow={true}
          placement="bottom"
          mask={{
            style: {
              boxShadow: 'inset 0 0 15px #333',
            },
          }}
        />
      </ConfigProvider>
    </AdminLayout>
  );
};

export default TypeManagement; 